import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { Destination } from '../../../../redux/models/statistics/destination';
import { DestinationType } from '../../../../redux/models/statistics/destination-type';
import { StatisticsType } from '../../../../redux/models/statistics/statistics-type';
import { TopDestination } from '../../../../redux/models/statistics/top-destination';
import { TopDestinationFilter } from '../../../../redux/models/statistics/top-destination-filter';
import { SetSubTopRatesCity, SetSubTopRatesHotel } from '../redux/actions/actions-dashboard';
import {
  subTopRatesLoading,
  selectStatisticsFilters,
  selectSubTopRatesCity,
  selectSubTopRatesHotel,
  selectTopRates
} from '../redux/selectors/dashboard-selector';
import { MainRates, Rate } from '../redux/states/dashboard-state';
import { StatisticsFilter } from '../../../../redux/models/statistics/statistics-filter';
import { DashboardService } from '../../../../redux/services/dashboard/dashboard.service';
import { selectAVCTACurrency } from '../../../../redux/reducers';

@Component({
  standalone: false,
  selector: 'app-top-destinations',
  templateUrl: './top-destinations.component.html',
  styleUrls: ['./top-destinations.component.scss']
})
export class TopDestinationsComponent implements OnChanges, OnDestroy, OnInit {
  @Input() statisticsType: StatisticsType;
  @Input() destinationType: DestinationType;
  @Input() filter: TopDestinationFilter;

  public DestinationType = DestinationType;
  public StatisticsType = StatisticsType;

  readonly destinationTypeHierarchy = {
    [DestinationType.COUNTRY]: DestinationType.CITY,
    [DestinationType.CITY]: DestinationType.HOTEL
  };

  loading = false;
  componentDestroyed = new Subject<any>();
  states: TopDestinationsState[] = [];
  topRates: MainRates;

  private currency: string;
  private statisticsFilter: StatisticsFilter;

  constructor(private readonly store: Store<any>, private readonly dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.states.push({
      destinationType: this.destinationType,
      selectedIndex: 0
    });
    this.store.select(selectAVCTACurrency).subscribe(currency => {
      this.currency = currency;
    });
    this.store.pipe(select(selectTopRates)).subscribe(data => {
      this.topRates = data;
      this.extractTopRatesData();
    });
    this.store.pipe(select(selectSubTopRatesHotel)).subscribe(data => {
      if (
        this.topRates &&
        this.states &&
        this.states.length > 1 &&
        this.states[this.states.length - 1].destinationType === DestinationType.HOTEL &&
        data
      ) {
        this.extractSubRatesData(data);
      }
    });
    this.store.pipe(select(selectSubTopRatesCity)).subscribe(data => {
      if (
        this.topRates &&
        this.states &&
        this.states.length > 1 &&
        this.states[this.states.length - 1].destinationType === DestinationType.CITY &&
        this.states[0].destinationType !== DestinationType.CITY &&
        data
      ) {
        this.extractSubRatesData(data);
      }
    });
    this.store.pipe(select(selectStatisticsFilters)).subscribe((filter: StatisticsFilter) => {
      this.statisticsFilter = filter;
    });
    this.store.pipe(select(subTopRatesLoading)).subscribe(loading => (this.loading = loading));
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.states = [];
    const currentState = {
      destinationType: this.destinationType,
      selectedIndex: 0
    };

    this.states.push(currentState);
    if (!_changes.filter) {
      this.extractTopRatesData();
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next(undefined);
    this.componentDestroyed.complete();
  }

  extractTopRatesData() {
    const state = this.states[this.states.length - 1];
    if (!this.topRates) {
      state.topDestinations = [];
      state.chartData = null;
      return;
    }

    switch (this.statisticsType) {
      case StatisticsType.NUMBER_OF_BOOKINGS:
        if (state.destinationType === DestinationType.CITY) {
          state.topDestinations = this.topRates.bookingsCity.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        } else if (state.destinationType === DestinationType.COUNTRY) {
          state.topDestinations = this.topRates.bookingsCountry.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        }
        break;
      case StatisticsType.GROSS_SALES:
        if (state.destinationType === DestinationType.CITY) {
          state.topDestinations = this.topRates.grossSalesCity.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        } else if (state.destinationType === DestinationType.COUNTRY) {
          state.topDestinations = this.topRates.grossSalesCountry.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        }
        break;
      case StatisticsType.NET_REVENUES:
        if (state.destinationType === DestinationType.CITY) {
          state.topDestinations = this.topRates.netRevenueCity.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        } else if (state.destinationType === DestinationType.COUNTRY) {
          state.topDestinations = this.topRates.netRevenueCountry.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        }
        break;
      default:
        if (state.destinationType === DestinationType.CITY) {
          state.topDestinations = this.topRates.markupRevenueCity.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        } else if (state.destinationType === DestinationType.COUNTRY) {
          state.topDestinations = this.topRates.markupRevenueCountry.map(rate => ({
            value: rate.value,
            percentFromTotal: rate.percentage,
            currency: this.currency,
            code: rate.name,
            name: rate.name
          }));
        }
        break;
    }
    state.chartData = this.getChartValues(state);
  }

  extractSubRatesData(rates?: Rate[]) {
    const state = this.states[this.states.length - 1];
    state.topDestinations = rates.map(rate => ({
      value: rate.value,
      percentFromTotal: rate.percentage,
      currency: this.currency,
      code: rate.name,
      name: rate.name
    }));
    state.chartData = this.getChartValues(state);
  }

  expandSubDestinations(index: number): void {
    const lastState = this.states[this.states.length - 1];
    if (lastState.selectedIndex !== index) {
      lastState.selectedIndex = index;
      lastState.chartData = this.getChartValues(lastState);
    }

    const parentDestination = lastState.topDestinations[index];

    const state = {
      destinationType: this.destinationTypeHierarchy[lastState.destinationType],
      parentDestination: {
        code: parentDestination.code,
        type: lastState.destinationType
      },
      selectedIndex: 0
    };
    this.states.push(state);

    if (state.destinationType === DestinationType.HOTEL) {
      this.store.dispatch(
        new SetSubTopRatesHotel(
          this.statisticsFilter,
          { statisticType: this.statisticsType, destinationType: state.destinationType },
          parentDestination.code
        )
      );
    } else {
      this.store.dispatch(
        new SetSubTopRatesCity(
          this.statisticsFilter,
          { statisticType: this.statisticsType, destinationType: state.destinationType },
          parentDestination.code
        )
      );
    }
  }

  backToParentDestinations(): void {
    this.states.pop();
  }

  getChartValues(state: TopDestinationsState): number[] {
    if (state.topDestinations?.length > 0) {
      const chartValues = state.topDestinations.map(topDestination => topDestination.percentFromTotal / 100);

      // Reorder chart values so selected destination will be first element. This is how pie chart works.
      const element = chartValues[state.selectedIndex];
      chartValues.splice(state.selectedIndex, 1);
      chartValues.splice(0, 0, element);

      return chartValues;
    }

    return null;
  }
}

interface TopDestinationsState {
  destinationType: DestinationType;
  parentDestination?: Destination;
  topDestinations?: TopDestination[];
  chartData?: number[];
  selectedIndex: number;
}